/**
 * Footer API service — fetches footer settings and menu from WordPress.
 */

import type { FooterData } from '../types/footer';
import { apiClient } from './client';

const FALLBACK_FOOTER: FooterData = {
  settings: {
    businessName: 'Operisoft',
    logo: '',
    logoDark: '',
    logoWidthDesktop: 150,
    logoWidthMobile: 100,
    description: 'Operisoft helps organizations accelerate digital transformation through Cloud, AI, Cybersecurity, DevOps, Compliance and Managed Services.\n\nWe build secure, scalable and intelligent platforms for enterprises worldwide.',
    social: [
      { platform: 'linkedin', url: 'https://linkedin.com/company/operisoft' },
      { platform: 'github', url: 'https://github.com/operisoft' },
      { platform: 'x', url: 'https://x.com/operisoft' },
      { platform: 'youtube', url: 'https://youtube.com/@operisoft' },
    ],
    copyright: `© ${new Date().getFullYear()} Operisoft. All Rights Reserved.`,
    cta: { text: 'Schedule Consultation', url: '/contact' },
    contact: {
      email: 'hello@operisoft.com',
      phone: '+91-XXXXXXXXXX',
      address: 'India',
      businessHours: 'Mon–Fri, 9 AM – 6 PM IST',
    },
    policies: [
      { label: 'Privacy Policy', url: '/privacy' },
      { label: 'Terms', url: '/terms' },
      { label: 'Cookies', url: '/cookies' },
      { label: 'Sitemap', url: '/sitemap.xml' },
    ],
  },
  watermark: {
    text: 'operisoft',
    textCase: 'lowercase',
    fontSize: 'clamp(8rem, 20vw, 22rem)',
    fontSizeMobile: '4rem',
    visibleMobile: true,
    color: '#bec8d7',
    opacity: 0.5,
    fontWeight: '900',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    spacing: '-0.02em',
    align: 'center',
  },
  menu: [
    {
      id: 1, label: 'What We Do', url: '#', children: [
        { id: 11, label: 'Services', url: '/services', target: '_self', children: [] },
        { id: 12, label: 'Solutions', url: '/solutions', target: '_self', children: [] },
        { id: 13, label: 'Industries', url: '/industries', target: '_self', children: [] },
        { id: 14, label: 'Partners', url: '/partners', target: '_self', children: [] },
        { id: 15, label: 'Resources', url: '/resources', target: '_self', children: [] },
        { id: 16, label: 'Case Studies', url: '/case-studies', target: '_self', children: [] },
      ],
    },
    {
      id: 2, label: 'Services', url: '#', children: [
        { id: 21, label: 'AWS Cloud', url: '/services/cloud', target: '_self', children: [] },
        { id: 22, label: 'Data & AI', url: '/services/ai', target: '_self', children: [] },
        { id: 23, label: 'Cybersecurity', url: '/services/security', target: '_self', children: [] },
        { id: 24, label: 'DevOps & Platform Engineering', url: '/services/devops', target: '_self', children: [] },
        { id: 25, label: 'Observability & IT Operations', url: '/services/operations', target: '_self', children: [] },
        { id: 26, label: 'Backup & DR (Business Continuity)', url: '/services/monitoring', target: '_self', children: [] },
      ],
    },
    {
      id: 3, label: 'Solutions', url: '#', children: [
        { id: 31, label: 'Mizan GRC Suite', url: '/solutions/mizan', target: '_self', children: [] },
        { id: 32, label: 'Cloud Platforms', url: '/solutions/cloudlabs', target: '_self', children: [] },
        { id: 33, label: 'Dark Web Monitoring Platform', url: '/solutions/darkweb', target: '_self', children: [] },
        { id: 34, label: 'Threat Analytics & SOC Toolkit', url: '/solutions/threat-analytics', target: '_self', children: [] },
        { id: 35, label: 'AI Applications', url: '/solutions/ai', target: '_self', children: [] },
      ],
    },
    {
      id: 4, label: 'Company', url: '#', children: [
        { id: 41, label: 'About', url: '/about', target: '_self', children: [] },
        { id: 42, label: 'Leadership', url: '/leadership', target: '_self', children: [] },
        { id: 43, label: 'Careers', url: '/careers', target: '_self', children: [] },
        { id: 44, label: 'Blog', url: '/blog', target: '_self', children: [] },
        { id: 45, label: 'Contact', url: '/contact', target: '_self', children: [] },
      ],
    },
    {
      id: 5, label: 'Contact', url: '#', children: [
        { id: 51, label: 'hello@operisoft.com', url: 'mailto:hello@operisoft.com', target: '_self', children: [] },
        { id: 52, label: '+91-XXXXXXXXXX', url: 'tel:+91XXXXXXXXXX', target: '_self', children: [] },
        { id: 53, label: 'India', url: '#', target: '_self', children: [] },
        { id: 54, label: 'Mon–Fri, 9AM–6PM', url: '#', target: '_self', children: [] },
        { id: 55, label: 'Schedule Consultation', url: '/contact', target: '_self', children: [] },
      ],
    },
  ],
};

export async function fetchFooterData(): Promise<FooterData> {
  try {
    const response = await apiClient.get<FooterData>('/wp-json/otpl/v1/footer');
    return response.data;
  } catch {
    return FALLBACK_FOOTER;
  }
}
